import { forwardRef, MouseEvent, ReactElement } from "react";

import {
  extendVariants,
  PopoverContent as NextUIPopoverContent,
  PopoverContentProps as NextUIPopoverContentProps,
} from "@nextui-org/react";

import { cn } from "@/utils/cn";

export type PopoverContentProps = NextUIPopoverContentProps & {
  variant?: "search" | "fullScreen";
};

const BasePopoverContent = extendVariants(NextUIPopoverContent, {
  variants: {
    variant: {
      search: cn(
        "sm:max-w-[calc(100dvw-3rem)] max-h-[calc(100dvh-10rem)] w-[calc(100dvw-3rem)] sm:w-full",
        "flex mx-auto"
      ),
      fullScreen:
        "!fixed !inset-0 !w-dvw !h-dvh !rounded-none !m-0 !top-0 !left-0 !transform-none",
    },
  },
  defaultVariants: {
    variant: "search",
  },
  compoundVariants: [
    {
      class: cn(
        "bg-gray-70 border border-solid border-gray-100",
        "overflow-hidden z-modal",
        "p-0",
        "text-md",
      ),
    },
  ],
});

// Generic ref (no element type restriction)
export const PopoverContent = forwardRef<ReactElement, PopoverContentProps>(
  ({ children, ...props }, ref) => {
    // iOS double-tap focus fix
    const isIOS =
      typeof navigator !== "undefined" &&
      /iPad|iPhone|iPod/.test(navigator.userAgent);

    // allow inputs to focus normally, block non-form clicks
    const handleMouseDown = (e: MouseEvent) => {
      if (isIOS) return;

      const target = e.target as HTMLElement;
      const tag = target.tagName.toLowerCase();
      const isFormElement =
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        target.closest("input, textarea, select, [contenteditable='true']");
      if (!isFormElement) e.preventDefault();
    };

    return (
      <BasePopoverContent
        ref={ref}
        onMouseDown={handleMouseDown}
        {...props}
      >
        {children}
      </BasePopoverContent>
    );
  },
);

PopoverContent.displayName = "PopoverContent";
